import React, {
    ComponentPropsWithRef,
    forwardRef,
    MutableRefObject,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { mergeProps, useDialog, useIsSSR, useModal, useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import {
    arrow,
    autoUpdate,
    flip as flipMiddleware,
    hide,
    offset as offsetMiddleware,
    Placement,
    shift,
    useFloating,
    VirtualElement,
} from '@floating-ui/react-dom';
import { createPortal } from 'react-dom';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useOverlay } from '../../hooks/useOverlay/useOverlay';
import { getCurrentBrowser } from '../../lib/useragent/getCurrentBrowser/getCurrentBrowser';
import { useLayerContext } from '../../lib/layerManager/layerManager';
import { LayerFocusScope } from '../../lib/layerManager/components/LayerFocusScope/LayerFocusScope';
import { roundByDPR } from '../../lib/dom/roundByDPR/roundByDPR';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';

import { PopupTail } from './Tail/Tail';
import { PopupPropsRaw } from './types';
import { size, limitValue, getSizeStyle } from './utils';

import './Popup.css';

export const cnPopup = cn('Popup');

const scrollClasses = getScrollbarClassName();

const defaultAutoFocusProps = {
    restoreFocus: true,
    contain: true,
    autoFocus: true,
};

// В jsdom некорректно высчитываются размеры элементов, из-за чего floating-ui считает,
// что триггер скрыт и скрывает попап. Поэтому детектим jsdom и не выставляем visibility: hidden в этом случае
// и не рисуем хвост, т.к. он почему-то ремаунтится
const IS_JSDOM = typeof window !== 'undefined' && window.navigator.userAgent.includes('jsdom');
const IS_SAFARI = getCurrentBrowser() === 'Safari';
const TAIL_SIZE = 10;
const AUTO_UPDATE =
    typeof window === 'undefined' || !window.ResizeObserver
        ? undefined
        : (reference: Element | VirtualElement, floating: HTMLElement, update: () => void) =>
              autoUpdate(reference, floating, update, {
                  animationFrame: true,
              });

function getTransform({ x, y }: { x: number | null; y: number | null }) {
    // Для низких PPI используем 2d translate, чтобы текст не размывался на Windows устройствах
    // https://nda.ya.ru/t/NL2w4je363mhHG
    return (typeof window !== 'undefined' ? window.devicePixelRatio : 1) < 2
        ? `translate(${roundByDPR(x ?? 0)}px, ${roundByDPR(y ?? 0)}px)`
        : `translate3d(${roundByDPR(x ?? 0)}px, ${roundByDPR(y ?? 0)}px, 0)`;
}

export const Popup = forwardRef<HTMLDivElement, PopupPropsRaw>(function Popup(props, forwardedRef) {
    const {
        isOpen,
        strategy = 'absolute',
        placement: initialPlacement = 'bottom',
        hasTail,
        offset = 5,
        crossOffset = 0,
        onClose,
        triggerRef,
        role = 'popup',
        ariaType = 'dialog',
        children,
        className,
        autoFocusProps = defaultAutoFocusProps,
        tailClassName,
        isModal,
        isDismissable = true,
        isAddedToLayerManager = true,
        onMouseEnter,
        onMouseLeave,
        containerPadding = 8,
        shouldCloseOnInteractOutside,
        maxWidth,
        maxHeight,
        flip = 'always',
        autoHide = true,
        shouldResize = true,
        matchTriggerWidth,
        isWithoutShadow = false,
    } = props;

    const maxHeightRef = useRef(maxHeight);
    const maxWidthRef = useRef(maxWidth);
    // В apply функции мидлвары size не меняется значение пропса, поэтому прокидываем с помощью ref
    useIsomorphicLayoutEffect(() => {
        maxHeightRef.current = maxHeight;
        maxWidthRef.current = maxWidth;
    }, [maxHeight, maxWidth]);

    const sizeSkinClass = getSizeSkin('m');
    const tailRef = useRef<SVGElement | null>(null);
    const [firstPlacement, setFirstPlacement] = useState<Placement>();
    const defaultShouldCloseOnInteractOutside = useCallback(
        (element: HTMLElement) => {
            return !triggerRef.current?.contains(element);
        },
        [triggerRef],
    );
    const state = useOverlayTriggerState({ isOpen: isOpen });
    const { mainPlacement, fallbackPlacements } = useMemo(() => {
        if (!Array.isArray(initialPlacement)) {
            if (flip === 'never') {
                return {
                    mainPlacement: initialPlacement,
                    fallbackPlacements: [],
                };
            }

            return {
                mainPlacement: firstPlacement ?? initialPlacement,
                fallbackPlacements: firstPlacement ? [firstPlacement] : undefined,
            };
        }

        if (flip === 'never') {
            return {
                mainPlacement: initialPlacement[0],
                fallbackPlacements: [],
            };
        }

        const [mainPlacement, ...specifiedFallbackPlacements] = initialPlacement;
        const defaultFallbackPlacements =
            specifiedFallbackPlacements.length > 0 ? specifiedFallbackPlacements : undefined;
        const fallbackPlacements = firstPlacement ? [firstPlacement] : defaultFallbackPlacements;

        return {
            mainPlacement: firstPlacement ?? mainPlacement,
            fallbackPlacements,
        };
    }, [initialPlacement, firstPlacement, flip]);

    const { x, y, refs, isPositioned, update, placement, middlewareData } = useFloating<Element>({
        open: isOpen,
        strategy,
        whileElementsMounted: AUTO_UPDATE,
        placement: mainPlacement,
        middleware: [
            offsetMiddleware({ mainAxis: hasTail ? offset + TAIL_SIZE : offset, crossAxis: crossOffset }),
            flipMiddleware({ fallbackPlacements, padding: containerPadding }),
            shift({ padding: containerPadding }),
            size({
                apply({ rects, availableHeight, availableWidth, elements }) {
                    if (!shouldResize) {
                        return;
                    }

                    Object.assign(elements.floating.style, {
                        maxHeight:
                            shouldResize !== 'width' ? (limitValue(maxHeightRef.current, availableHeight) ?? '') : '',
                        maxWidth:
                            shouldResize !== 'height' ? (limitValue(maxWidthRef.current, availableWidth) ?? '') : '',
                        width: matchTriggerWidth ? getSizeStyle(rects.reference.width) : '',
                    });
                },
                padding: containerPadding,
            }),
            arrow(
                // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                { element: tailRef as MutableRefObject<HTMLElement | null>, padding: containerPadding },
            ),
            hide({ padding: containerPadding }),
        ],
    });

    const offsetBlockStyles = useMemo(() => {
        const offsetBlockSize = offset + (hasTail ? TAIL_SIZE : 0);
        const mainPlacement = placement.split('-')[0];

        if (mainPlacement === 'right') {
            return {
                left: `-${offsetBlockSize}px`,
                width: `${offsetBlockSize}px`,
            };
        }

        if (mainPlacement === 'left') {
            return {
                right: `-${offsetBlockSize}px`,
                width: `${offsetBlockSize}px`,
            };
        }

        if (mainPlacement === 'bottom') {
            return {
                top: `-${offsetBlockSize}px`,
                height: `${offsetBlockSize}px`,
            };
        }

        if (mainPlacement === 'top') {
            return {
                bottom: `-${offsetBlockSize}px`,
                height: `${offsetBlockSize}px`,
            };
        }

        return {};
    }, [hasTail, placement, offset]);

    useIsomorphicLayoutEffect(() => {
        if (state.isOpen && flip === 'once' && x !== null && y !== null && !firstPlacement && isPositioned) {
            setFirstPlacement(placement);
        }
    }, [firstPlacement, flip, state.isOpen, x, y, placement, refs.floating, refs.reference, isPositioned]);

    useEffect(() => {
        if (!state.isOpen) {
            setFirstPlacement(undefined);
        }
    }, [state.isOpen]);
    const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {};

    useIsomorphicLayoutEffect(() => {
        refs.setReference(triggerRef.current);
    });

    const popupRefCallback = useCallback(
        (node: HTMLDivElement) => {
            refs.setFloating(node);
            if (typeof forwardedRef === 'function') {
                forwardedRef(node);
            } else if (forwardedRef !== null) {
                forwardedRef.current = node;
            }
        },
        [forwardedRef, refs],
    );

    const tailRefCallback = useCallback(
        (node: SVGSVGElement) => {
            tailRef.current = node;
            update();
        },
        [update],
    );
    const { overlayProps: triggerOverlayInternalProps } = useOverlayTrigger({ type: ariaType }, state, refs.reference);

    const layerManager = useLayerContext();
    const popupZIndex = layerManager.zIndexForContextPopups;

    const popupId = useId();

    const { overlayProps } = useOverlay(
        {
            isOpen: state.isOpen,
            onClose,
            isLayer: false,
            isModal,
            isDismissable: isModal || isDismissable,
            isAddedToLayerManager,
            shouldCloseOnInteractOutside:
                shouldCloseOnInteractOutside ??
                (!isModal && isDismissable ? defaultShouldCloseOnInteractOutside : undefined),
            zIndex: popupZIndex,
            parentLayerId: layerManager.id,
            id: popupId,
        },
        refs.floating,
    );

    const { modalProps } = useModal({ isDisabled: !isModal });
    const { dialogProps } = useDialog({ role: 'dialog' }, refs.floating);

    const hoverProps = useMemo(
        () => ({
            onMouseEnter,
            onMouseLeave,
        }),
        [onMouseEnter, onMouseLeave],
    );

    const isSsr = useIsSSR();
    if (isSsr) {
        // На сервере невозможно отрисовать портал
        return null;
    }

    const portalInto = layerManager.contextPopupsContainer;
    if (!state.isOpen || !portalInto) {
        return null;
    }

    const isPopupHidden = !IS_JSDOM && autoHide && middlewareData.hide?.referenceHidden;

    if (!autoFocusProps.autoFocus) {
        // useDialog проставляет tabIndex=-1, из-за чего активируется фокус на попапе

        // eslint-disable-next-line react-hooks/immutability -- Автоматический игнор в рамках DIRECT-288723
        delete dialogProps.tabIndex;
    }

    return createPortal(
        <div popup-container="true">
            <LayerFocusScope autoFocusProps={autoFocusProps} isOpen={isOpen} contextPopupId={popupId}>
                <div
                    {...mergeProps(
                        overlayProps,
                        triggerOverlayInternalProps,
                        modalProps,
                        isModal || isDismissable ? dialogProps : {},
                        hoverProps,
                    )}
                    className={cnPopup(
                        {
                            role,
                            placement: placement.split('-')[0],
                            'simple-shadow': IS_SAFARI,
                            'without-shadow': isWithoutShadow,
                        },
                        [scrollClasses, sizeSkinClass, className],
                    )}
                    style={{
                        position: strategy,
                        zIndex: popupZIndex,
                        top: x === null ? undefined : 0,
                        left: y === null ? undefined : 0,
                        transform: getTransform({ x, y }),

                        // Стили отвечающие за скрытие попапа
                        visibility: isPopupHidden ? 'hidden' : undefined,
                        opacity: isPopupHidden ? '0' : undefined,
                        pointerEvents: isPopupHidden ? 'none' : undefined,
                    }}
                    ref={popupRefCallback}
                    data-testid={props['data-testid'] || 'Popup'}
                    focus-fallback="true"
                >
                    {(onMouseEnter || onMouseLeave) && (
                        <div className={cnPopup('offsetArea')} style={offsetBlockStyles} />
                    )}
                    {children}
                    {hasTail && !IS_JSDOM && !isNaN(arrowX ?? 0) && !isNaN(arrowY ?? 0) && (
                        <PopupTail
                            style={{
                                left: arrowX,
                                top: arrowY,
                            }}
                            ref={tailRefCallback}
                            placement={placement}
                            className={cnPopup('tail', [tailClassName])}
                        />
                    )}
                </div>
            </LayerFocusScope>
        </div>,
        portalInto,
    );
});

export type PopupProps = ComponentPropsWithRef<typeof Popup>;
